(() => {
  if (customElements.get('zxp-candies-carousel')) return;

  class ZxpCandiesCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-candies-track]');
      this.slides = Array.from(this.querySelectorAll('[data-candies-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-candies-dot]'));
      this.previousButton = this.querySelector('[data-candies-prev]');
      this.nextButton = this.querySelector('[data-candies-next]');

      if (!this.track || !this.slides.length || this.dataset.candiesReady === 'true') return;

      this.dataset.candiesReady = 'true';
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };

      this.previousButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1), options);
      this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1), options);
      this.track.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.track.addEventListener('scroll', () => this.queueUpdate(), { ...options, passive: true });

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index), options);
      });

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.activeIndex = 0;
      this.update();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      delete this.dataset.candiesReady;
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.activeIndex - 1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.activeIndex + 1);
      }
    }

    goTo(index) {
      const nextIndex = Math.max(0, Math.min(index, this.maxStartIndex()));
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

      this.track.scrollTo({ left: this.slides[nextIndex].offsetLeft, behavior });
      this.activeIndex = nextIndex;
      this.updateControls();
    }

    queueUpdate() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }

    update() {
      const trackLeft = this.track.getBoundingClientRect().left;
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      this.activeIndex = nearestIndex;
      this.updateControls();
    }

    updateControls() {
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth - 2);
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft >= maxScroll;
      const maxStartIndex = this.maxStartIndex();

      if (this.previousButton) this.previousButton.disabled = atStart;
      if (this.nextButton) this.nextButton.disabled = atEnd;

      this.dots.forEach((dot, index) => {
        const isActive = index === this.activeIndex;
        dot.hidden = index > maxStartIndex;
        dot.classList.toggle('zxp-candies-is-active', isActive);
        dot.setAttribute('aria-pressed', String(isActive));
      });
    }

    maxStartIndex() {
      const slideWidth = this.slides[0]?.getBoundingClientRect().width || this.track.clientWidth;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      return Math.min(this.slides.length - 1, Math.max(0, Math.round(maxScroll / slideWidth)));
    }
  }

  customElements.define('zxp-candies-carousel', ZxpCandiesCarousel);
})();
